export type AnalysisFunnelMode = 'identify' | 'diagnose' | 'weed'

export interface AnalysisFunnelEnvelopeInput {
  funnelId?: string | null
  attemptId?: string | null
  analysisId?: string | null
  originLawnId?: string | null
  selectedLawnId?: string | null
  zipMatch?: boolean | null
  mode: AnalysisFunnelMode
  source: string
  imageRoles?: readonly string[] | string | null
  rawConfidence?: number | null
  displayConfidence?: number | null
  gatingReasons?: readonly string[] | string | null
  safetyProof?: string | null
  model?: string | null
  promptVersion?: string | null
  promptStrategy?: string | null
  pipelineVersion?: string | null
  authState?: 'anonymous' | 'authenticated' | 'unknown'
  actualDestination?: string | null
  /**
   * Entry attribution. `source` keeps its historical per-call-site meaning; the
   * four below are unambiguous and always mean the same thing on every event.
   * The browser fills them automatically — see the property-split note in
   * lib/analytics/entry-source.ts. Server callers leave them null.
   */
  entrySurface?: string | null
  entryPathname?: string | null
  routeSurface?: string | null
  fromTool?: string | null
  /** anonymous | email_unlocked | registered_free | premium — the real
   *  monetization rung, unlike `auth_state`. See lib/analytics/user-tier.ts. */
  userTier?: string | null
}

export type AnalysisFunnelEnvelopeProperties = Record<
  string,
  string | number | boolean | null | undefined
>

/**
 * Stable join contract for the Identify, Diagnose, and Weed funnels. This
 * module deliberately has no browser imports so API routes and clients use
 * exactly the same field names and unknown-value semantics.
 */
export function analysisFunnelEnvelope(
  input: AnalysisFunnelEnvelopeInput,
): AnalysisFunnelEnvelopeProperties {
  return {
    funnel_id: input.funnelId ?? null,
    attempt_id: input.attemptId ?? null,
    analysis_id: input.analysisId ?? null,
    origin_lawn_id: input.originLawnId ?? null,
    selected_lawn_id: input.selectedLawnId ?? null,
    zip_match: input.zipMatch ?? null,
    mode: input.mode,
    source: input.source,
    image_roles: typeof input.imageRoles === 'string'
      ? input.imageRoles
      : input.imageRoles?.join(',') ?? null,
    raw_confidence: input.rawConfidence ?? null,
    display_confidence: input.displayConfidence ?? null,
    gating_reasons: typeof input.gatingReasons === 'string'
      ? input.gatingReasons
      : input.gatingReasons?.join(',') ?? null,
    safety_proof: input.safetyProof ?? null,
    model: input.model ?? null,
    prompt_version: input.promptVersion ?? null,
    prompt_strategy: input.promptStrategy ?? null,
    pipeline_version: input.pipelineVersion ?? null,
    auth_state: input.authState ?? 'unknown',
    actual_destination: input.actualDestination ?? null,
    entry_surface: input.entrySurface ?? null,
    entry_pathname: input.entryPathname ?? null,
    route_surface: input.routeSurface ?? null,
    from_tool: input.fromTool ?? null,
    user_tier: input.userTier ?? null,
  }
}
